import { z } from 'zod'

/**
 * O histórico de um jogador, como o servidor o serve em `/api/history/:address`.
 *
 * É a MESMA informação do `MatchRecordV3` on-chain — o servidor só a lê do
 * índice em vez de pedir ao RPC — e por isso a forma é a do registro: cada
 * campo daqui existe na conta, e o cliente continua conferindo o replay contra
 * eles exatamente como conferia quando os lia direto da chain. Nada aqui é
 * palavra do servidor que a chain não sustente; quem duvidar refaz a consulta
 * pelo RPC, e o cliente faz isso sozinho quando o servidor falha.
 *
 * Tudo que é bytes viaja em hex e tudo que é chave em base58 — as formas que o
 * projeto já usa nos logs e nas URLs. Lamports vão como STRING: `number`
 * perde precisão acima de 2^53.
 */
export const HistoricoRegistroSchema = z.object({
  /** `match_id` em hex minúsculo, 32 caracteres. */
  matchId: z.string().regex(/^[0-9a-f]{32}$/),
  /** PDA do `MatchRecord`, em base58. É o link "ver na chain". */
  pda: z.string().min(32),
  winner: z.string().min(32),
  loser: z.string().min(32),
  /** Quem criou a mesa: o JOGADOR 0 do replay. */
  creator: z.string().min(32),
  /** O endereço consultado venceu esta partida? */
  won: z.boolean(),
  pot: z.string().regex(/^\d+$/),
  /**
   * O que o vencedor de fato recebeu, lido do evento `MatchSettled` da
   * liquidação. Não é recalculável com a divisão de hoje — `set_splits` muda
   * com o tempo — e é por isso que vem daqui em vez de o cliente somar.
   */
  prize: z.string().regex(/^\d+$/),
  /** Segundos desde a época, como na conta. */
  settledAt: z.number().int().nonnegative(),
  /** SHA-256 do replay, hex de 64 caracteres. */
  resultHash: z.string().regex(/^[0-9a-f]{64}$/),
  nonceCreator: z.string().regex(/^[0-9a-f]{64}$/),
  nonceOpponent: z.string().regex(/^[0-9a-f]{64}$/),
  replayLen: z.number().int().positive(),
  /**
   * Os pontos de rank que ESTA partida rendeu ao vencedor, em lamports, como
   * string pelo mesmo motivo de `pot`. Já com o decaimento por repetição do
   * par aplicado dentro da semana da partida, e `'0'` para derrota ou para
   * pote abaixo da aposta mínima. Ver `pontosPorPartida` no servidor.
   *
   * Opcional: é a única coisa aqui que a chain não sustenta sozinha — é conta
   * do servidor, ausente quando ele não a fez.
   */
  pontos: z.string().regex(/^\d+$/).optional(),
})
export type HistoricoRegistro = z.infer<typeof HistoricoRegistroSchema>

export const HistoricoRespostaSchema = z.object({
  ok: z.literal(true),
  matches: z.array(HistoricoRegistroSchema),
  /** Quando o índice foi reconciliado com a chain pela última vez. Nulo se nunca. */
  reconciledAt: z.number().int().nullable(),
})
export type HistoricoResposta = z.infer<typeof HistoricoRespostaSchema>
